import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SessionIndicator — small chip near the top-bar avatar showing how long
 * until the user is signed out for INACTIVITY (the clock that actually
 * logs them out — the 30-min idle window the auto-logout enforces), not
 * the auto-renewing access-token clock. Resets whenever the user is active.
 *
 * Three states (color escalates as the idle deadline approaches):
 *   • Healthy (>10 min): subtle, slate. Shows "Session 24m".
 *   • Warn (5-10 min): amber. Same label, different colour.
 *   • Critical (<5 min): rose, animate-pulse — matches the 5-min idle
 *     warning toast; a nudge to move the mouse before sign-out.
 *
 * Hidden when there's no active idle timer (pre-login / hook disabled).
 *
 * Industry parity: Microsoft 365 admin, Salesforce Lightning, AWS Console
 * all show an idle session-timeout countdown for authenticated dashboards.
 *
 * 2026-06-22 — added an Info button + click-to-open popover so the
 * meaning of "Session 29m" is discoverable on touch devices (the
 * native `title` attribute only fires on hover). Popover also shows
 * **time signed in this tab** (sourced from sessionStorage stamped on
 * first mount) so the user can answer "how long have I been working
 * for?" without doing the math against their login time.
 */
import { Info, ShieldCheck, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getIdleExpiryEpochSeconds } from '@/lib/idleTimeout';
import { useSessionTimer } from '@/lib/sessionTimer';
import { cn } from '@/lib/cn';
// Key stamped at first mount of this component once a user is logged in;
// cleared when the tab closes (sessionStorage semantics). On a re-login in
// the same tab the value persists — fine, since the popover only shows
// "approximate time in this tab session" and a single user wouldn't be
// confused by a 5-second offset.
const SESSION_START_KEY = 'styxdesk.session.startedAt';
function formatRemaining(seconds) {
    if (seconds <= 0)
        return 'expired';
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remMin = minutes - hours * 60;
    return remMin === 0 ? `${hours}h` : `${hours}h ${remMin}m`;
}
function formatElapsed(seconds) {
    if (seconds < 60)
        return 'less than a minute';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    const hours = Math.floor(minutes / 60);
    const remMin = minutes - hours * 60;
    return remMin === 0
        ? `${hours} hour${hours === 1 ? '' : 's'}`
        : `${hours}h ${remMin}m`;
}
/** Read or stamp the session-start epoch ms (sessionStorage). Tab-scoped:
 *  a fresh tab → fresh start time. Survives reloads within the tab. */
function useSessionStartedAt() {
    const [startedAt, setStartedAt] = useState(() => {
        try {
            const raw = sessionStorage.getItem(SESSION_START_KEY);
            if (raw) {
                const n = Number(raw);
                if (Number.isFinite(n) && n > 0)
                    return n;
            }
        }
        catch {
            // sessionStorage unavailable (private mode quirks) — fall through.
        }
        return null;
    });
    useEffect(() => {
        if (startedAt !== null)
            return;
        const now = Date.now();
        try {
            sessionStorage.setItem(SESSION_START_KEY, String(now));
        }
        catch {
            // ignore — popover just won't show "signed in for X"
        }
        setStartedAt(now);
    }, [startedAt]);
    return startedAt;
}
export function SessionIndicator() {
    const { secondsRemaining, isExpiringSoon, isCritical } = useSessionTimer({
        getExpiry: getIdleExpiryEpochSeconds,
    });
    const startedAt = useSessionStartedAt();
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    // Close the popover on outside-click / Escape — standard popover hygiene.
    useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    // useSessionTimer ticks every second, so this is recomputed every tick
    // along with secondsRemaining — keeps the "signed in for X" line live.
    const elapsedSeconds = useMemo(() => {
        if (startedAt === null)
            return null;
        return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
        // secondsRemaining is the implicit tick dep below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startedAt, secondsRemaining]);
    // Hide the chip entirely when there's no active idle timer (pre-login,
    // hook disabled). The shell layout keeps working because the chip is
    // inline and just disappears.
    if (secondsRemaining === null)
        return null;
    const tone = isCritical
        ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-300'
        : isExpiringSoon
            ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300'
            : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300';
    const remainingLabel = formatRemaining(secondsRemaining);
    const elapsedLabel = elapsedSeconds !== null ? formatElapsed(elapsedSeconds) : null;
    return (_jsxs("div", { ref: popoverRef, className: "relative hidden md:inline-flex", children: [_jsxs("span", { className: cn('inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium', tone, isCritical && 'animate-pulse'), 
                // Native tooltip for desktop power-users (hover) — the popover
                // below is the discoverable equivalent for touch.
                title: `You'll be signed out in ${remainingLabel} if you stay inactive. Any activity (move the mouse, type) resets the timer.${elapsedLabel ? `\n\nSigned in for ${elapsedLabel}.` : ''}`, children: [_jsx(ShieldCheck, { size: 11 }), "Session ", remainingLabel, _jsx("button", { type: "button", "aria-label": "What does this mean?", "aria-expanded": open, onClick: () => setOpen((v) => !v), className: "ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-current", children: _jsx(Info, { size: 11 }) })] }), open && (_jsxs("div", { className: "absolute right-0 top-full z-50 mt-1 w-72 rounded-md border border-slate-200 bg-white p-3 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900", children: [_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsx("div", { className: "font-semibold text-slate-800 dark:text-slate-100", children: "Your session" }), _jsx("button", { type: "button", "aria-label": "Close", onClick: () => setOpen(false), className: "rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200", children: _jsx(X, { size: 12 }) })] }), _jsxs("ul", { className: "space-y-1 text-slate-600 dark:text-slate-300", children: [_jsxs("li", { children: [_jsx("span", { className: "font-medium text-slate-700 dark:text-slate-200", children: "Auto-logout in:" }), ' ', remainingLabel, ' ', _jsx("span", { className: "text-slate-500 dark:text-slate-400", children: "(resets on any activity \u2014 move the mouse, type, scroll)" })] }), elapsedLabel && (_jsxs("li", { children: [_jsx("span", { className: "font-medium text-slate-700 dark:text-slate-200", children: "Signed in for:" }), ' ', elapsedLabel] })), _jsx("li", { className: "pt-1 text-[10px] italic text-slate-500 dark:text-slate-400", children: "We sign you out after 30 minutes of inactivity to keep your account safe \u2014 same as Microsoft 365 / Salesforce / banking dashboards." })] })] }))] }));
}
